import * as React from "react";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Typography,
  Grid,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
  TextField,
  Divider,
  CircularProgress,
  Alert,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Link,
  Tooltip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import TextareaAutosize from "@mui/material/TextareaAutosize";
import SignatureCanvas from "react-signature-canvas";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import moment from "moment";
import axios from "axios";
import Swal from "sweetalert2";
import SignatureField from "../../../components/SignatureField";

const fmt = (d) => (d ? moment(d).format("MM/DD/YYYY") : "—");

const expectedRows = [
  { key: "mentalhealthsymptoms", label: "Mental Health\nSymptoms" },
  { key: "mentalhealthserviceneeds", label: "Mental Health\nService Needs" },
  { key: "drugsalcohol", label: "Drugs/Alcohol" },
  { key: "vocational", label: "Vocational" },
  { key: "educational", label: "Educational" },
  { key: "social", label: "Social" },
  { key: "interpersonal", label: "Interpersonal" },
  { key: "selfcare", label: "Self-Care" },
  { key: "medical", label: "Medical" },
  { key: "dental", label: "Dental" },
  { key: "financial", label: "Financial" },
  { key: "housing", label: "Housing" },
  { key: "transportation", label: "Transportation" },
  { key: "other", label: "Other" },
];

const staffRoles = [
  { key: "staffMHProfessional", label: "MH Professional" },
  { key: "staffMHPractitioner", label: "MH Practitioner" },
  { key: "staffRehabWorker", label: "Rehab Worker" },
  { key: "staffRN", label: "RN" },
];

const supervisorRoles = [
  { key: "supervisorMHProfessional", label: "MH Professional" },
  { key: "supervisorMHPractitioner", label: "MH Practitioner" },
  { key: "supervisorRehabWorker", label: "Rehab Worker" },
  { key: "supervisorRN", label: "RN" },
];

const API_BASE_URL =
  process.env.NEXT_PUBLIC_API_BASE_URL ||
  process.env.REACT_APP_API_URL ||
  "https://neuroez4.devforhealth.com/api";

async function fetchJSONSafe(url, options = {}) {
  const res = await fetch(url, {
    headers: { Accept: "application/json", ...(options.headers || {}) },
    ...options,
  });
  let data = {};
  try {
    const text = await res.text();
    data = text ? JSON.parse(text) : {};
  } catch {
    data = {};
  }
  return { ok: res.ok, status: res.status, data };
}

const pickText = (...vals) => {
  for (const v of vals) {
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return "";
};

const ARMHSFunctionalAssessmentPreview = ({
  patientFormId: propPatientFormId,
  id,
  data = {},
  onClose,
}) => {
  const patientFormId =
    propPatientFormId || id || data?.patientFormId || data?.id;

  const [form, setForm] = React.useState({});
  const [staff, setStaff] = React.useState({});
  const [formMeta, setFormMeta] = React.useState(null); // full form record
  const [signatures, setSignatures] = React.useState({});
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  const [patientSigLink, setPatientSigLink] = React.useState("");
  const [openClientSignatureModal, setOpenClientSignatureModal] =
    React.useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = React.useState(false);
  const sigCanvasRef = React.useRef(null);

  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));
  const LABEL_COL_WIDTH = 340;

  const openClientModal = () => setOpenClientSignatureModal(true);
  const closeClientModal = () => {
    setOpenClientSignatureModal(false);
    if (sigCanvasRef.current) {
      sigCanvasRef.current.clear();
    }
  };

  const handleSaveClientSignature = () => {
    if (!sigCanvasRef.current) return;

    const dataUrl = sigCanvasRef.current.getCanvas().toDataURL("image/png");
    setPatientSigLink(dataUrl);
    setSignatures((prev) => ({ ...prev, client: dataUrl }));

    closeClientModal();
  };

  const handleDeleteClientSignature = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "Do you want to remove this signature?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, remove it!",
      didOpen: () => {
        const swalEl = document.querySelector(".swal2-container");
        if (swalEl) {
          swalEl.style.zIndex = "9999";
        }
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        setPatientSigLink("");
        setSignatures((prev) => ({ ...prev, client: "" }));

        try {
          const token = localStorage.getItem("neuro_patientportal_auth_token");
          const client = axios.create({
            baseURL: API_BASE_URL,
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
              Accept: "application/json",
              "Content-Type": "application/json",
            },
          });

          let payload;
          if (formMeta) {
            payload = {
              ...formMeta,
              Id: formMeta.Id ?? formMeta.id ?? patientFormId,
              patientFormId: patientFormId,
              clientsBase64: "",
              clientsURL: "",
              patientSignatureURL: "",
              clientSignatureURL: "",
              imageURLClientSignature: "",
            };
          } else {
            // fallback minimal
            payload = {
              patientFormId: patientFormId,
              clientsBase64: "",
              clientsURL: "",
            };
          }

          await client.post("/Forms/AddPatientForm", payload);

          if (formMeta) {
            setFormMeta((prev) =>
              prev
                ? {
                    ...prev,
                    clientsBase64: "",
                    clientsURL: "",
                    patientSignatureURL: "",
                    clientSignatureURL: "",
                    imageURLClientSignature: "",
                  }
                : prev
            );
          }
          setForm((prev) =>
            prev
              ? {
                  ...prev,
                  clientsBase64: "",
                  clientsURL: "",
                  patientSignatureURL: "",
                  clientSignatureURL: "",
                  imageURLClientSignature: "",
                }
              : prev
          );
          setStaff((prev) =>
            prev
              ? {
                  ...prev,
                  clientsBase64: "",
                  clientsURL: "",
                  patientSignatureURL: "",
                  clientSignatureURL: "",
                  imageURLClientSignature: "",
                }
              : prev
          );

          Swal.fire({
            title: "Deleted!",
            text: "Signature has been removed.",
            icon: "success",
            timer: 1500,
            showConfirmButton: false,
            didOpen: () => {
              const swalEl = document.querySelector(".swal2-container");
              if (swalEl) {
                swalEl.style.zIndex = "9999";
              }
            },
          });
        } catch (e) {
          console.error(e);
          Swal.fire({
            title: "Error!",
            text: "Failed to remove signature.",
            icon: "error",
            didOpen: () => {
              const swalEl = document.querySelector(".swal2-container");
              if (swalEl) {
                swalEl.style.zIndex = "9999";
              }
            },
          });
        }
      }
    });
  };

  const handleSaveForm = async () => {
    if (!patientFormId) return;
    try {
      const token = localStorage.getItem("neuro_patientportal_auth_token");
      const client = axios.create({
        baseURL: API_BASE_URL,
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });

      const isBase64 =
        patientSigLink &&
        typeof patientSigLink === "string" &&
        patientSigLink.startsWith("data:");

      let payload;
      if (formMeta) {
        payload = {
          ...formMeta,
          Id: formMeta.Id ?? formMeta.id ?? patientFormId,
          patientFormId: patientFormId,
          ...(isBase64 && { clientsBase64: patientSigLink }),
          // NOTE: do not blank clientsURL here
        };
      } else {
        // fallback minimal
        payload = {
          patientFormId: patientFormId,
          ...(isBase64 && { clientsBase64: patientSigLink }),
        };
      }

      await client.post("/Forms/AddPatientForm", payload);

      if (formMeta && isBase64) {
        setFormMeta((prev) =>
          prev ? { ...prev, clientsBase64: patientSigLink } : prev
        );
      }
      if (isBase64) {
        setForm((prev) =>
          prev ? { ...prev, clientsBase64: patientSigLink } : prev
        );
        setStaff((prev) =>
          prev ? { ...prev, clientsBase64: patientSigLink } : prev
        );
      }

      setShowSuccessAlert(true);
    } catch (e) {
      console.error(e);
      Swal.fire({
        title: "Error!",
        text: "Failed to save form.",
        icon: "error",
        didOpen: () => {
          const swalEl = document.querySelector(".swal2-container");
          if (swalEl) {
            swalEl.style.zIndex = "9999";
          }
        },
      });
    }
  };

  const handleClose = () => {
    setShowSuccessAlert(false);

    // Try calling onClose if it exists
    if (onClose) {
      onClose();
    } else {
      // Fallback: Try to close any parent dialog/modal
      try {
        const backdrop = document.querySelector(".MuiBackdrop-root");
        if (backdrop) {
          backdrop.click();
        } else {
          // Try to find close button
          const closeButton = document.querySelector('[aria-label="close"]');
          if (closeButton) {
            closeButton.click();
          } else {
            // Last resort: reload or go back
            window.history.back();
          }
        }
      } catch (err) {
        console.error("Could not close form:", err);
        // Reload as last resort
        window.location.reload();
      }
    }
  };

  React.useEffect(() => {
    let active = true;
    const revokeList = [];

    const getPreviewLink = async (path, token) => {
      try {
        if (!path) return "";
        if (/^(https?:|data:|blob:)/i.test(path)) return path;

        const url = `${API_BASE_URL}/PatientDocument/ViewPatientDocument?path=${encodeURIComponent(
          path
        )}`;
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return "";

        const ct = (res.headers.get("content-type") || "").toLowerCase();
        if (ct.includes("text/plain")) {
          const link = await res.text();
          return link || "";
        }

        const blob = await res.blob();
        if (blob && blob.size > 0) {
          const objUrl = URL.createObjectURL(blob);
          revokeList.push(objUrl);
          return objUrl;
        }
        return "";
      } catch {
        return "";
      }
    };

    const fetchSignatures = async (formData, staffData, token) => {
      const loaded = {};

      // 1) Base64 fields first
      const base64Candidate =
        formData?.clientsBase64 ||
        staffData?.clientsBase64 ||
        formData?.clientSignatureBase64 ||
        staffData?.clientSignatureBase64 ||
        formData?.clientBase64 ||
        staffData?.clientBase64;

      if (
        base64Candidate &&
        typeof base64Candidate === "string" &&
        base64Candidate.toLowerCase().includes("base64")
      ) {
        loaded.client = base64Candidate;
        if (active) setPatientSigLink(base64Candidate);
      } else {
        // 2) Fallback to URL/path fields
        const clientPath = pickText(
          formData?.clientsURL,
          formData?.clientSignatureURL,
          formData?.patientSignatureURL,
          staffData?.clientsURL,
          staffData?.clientSignatureURL,
          staffData?.patientSignatureURL
        );

        if (clientPath) {
          const link = await getPreviewLink(clientPath, token);
          loaded.client = link;
          if (active) setPatientSigLink(link);
        }
      }

      if (active) setSignatures(loaded);
    };

    const load = async () => {
      setLoading(true);
      setError("");
      setForm({});
      setStaff({});
      setFormMeta(null);
      setSignatures({});
      setPatientSigLink("");

      try {
        const token = localStorage.getItem(
          "neuro_patientportal_auth_token"
        );
        if (!token) throw new Error("Authentication token missing.");

        const headers = { Authorization: `Bearer ${token}` };

        const [formRes, staffRes] = await Promise.all([
          fetchJSONSafe(
            `${API_BASE_URL}/Forms/GetPatientFormsbyId?Id=${patientFormId}`,
            { headers }
          ),
          fetchJSONSafe(
            `${API_BASE_URL}/Rehabilitative/getStaffassesmentById?patientFormId=${patientFormId}`,
            { headers }
          ),
        ]);

        if (!active) return;

        if (!formRes.ok)
          throw new Error(`Forms request failed (${formRes.status})`);
        if (!staffRes.ok)
          throw new Error(
            `Staff assessment request failed (${staffRes.status})`
          );

        const formData = Array.isArray(formRes.data)
          ? formRes.data[0] || {}
          : formRes.data || {};
        const staffData = Array.isArray(staffRes.data)
          ? staffRes.data[0] || {}
          : staffRes.data || {};

        setForm(formData);
        setStaff(staffData);
        setFormMeta(formData || null);

        await fetchSignatures(formData, staffData, token);
      } catch (e) {
        if (active) setError(e.message || "Failed to load data");
      } finally {
        if (active) setLoading(false);
      }
    };

    if (patientFormId) load();

    return () => {
      active = false;
      try {
        revokeList.forEach(
          (u) => u.startsWith("blob:") && URL.revokeObjectURL(u)
        );
      } catch {}
    };
  }, [patientFormId]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        p={5}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  const title = "ARMHS Functional Assessment";
  const dos = staff.assessmentDate || form.assessmentDate || null;
  const fileVal = form.accountNo ?? "—";
  const nameVal = form.patientName || "—";
  const dobVal = form.dob || staff.patientDOB || null;
  const clientID = form.patientId || staff.patientID || "—";

  const timeFA =
    staff.initial || staff.intial
      ? "Initial"
      : staff.discharge
      ? "Discharge"
      : staff.ongoing
      ? "Ongoing"
      : "—";

  const faDate =
    staff.functionalDate ||
    staff.assessmentDate ||
    form.assessmentDate ||
    null;
  const diagnosticDate = staff.diagnosticDate || null;

  const statusByKey = {
    mentalhealthsymptoms: staff.symptomsStatus,
    mentalhealthserviceneeds: staff.servicesStatus,
    drugsalcohol: staff.drugsalcoholStatus,
    vocational: staff.vocationalStatus,
    educational: staff.educationalStatus,
    social: staff.socialStatus,
    interpersonal: staff.interpersonalStatus,
    selfcare: staff.selfcareStatus,
    medical: staff.medicalStatus,
    dental: staff.dentalStatus,
    financial: staff.financialStatus,
    housing: staff.housingStatus,
    transportation: staff.transportationStatus,
    other: staff.otherStatus,
  };

  const funcByKey = {
    mentalhealthsymptoms: staff.symptomsFunctioning,
    mentalhealthserviceneeds: staff.servicesFunctioning,
    drugsalcohol: staff.drugsalcoholFunctioning,
    vocational: staff.vocationalFunctioning,
    educational: staff.educationalFunctioning,
    social: staff.socialFunctioning,
    interpersonal: staff.interpersonalFunctioning,
    selfcare: staff.selfcareFunctioning,
    medical: staff.medicalFunctioning,
    dental: staff.dentalFunctioning,
    financial: staff.financialFunctioning,
    housing: staff.housingFunctioning,
    transportation: staff.transportationFunctioning,
    other: staff.otherFunctioning,
  };

  const linkByKey = {
    mentalhealthsymptoms: staff.linkTosymptoms,
    mentalhealthserviceneeds: staff.linkToservices,
    drugsalcohol: staff.linkTodrugsalcohol,
    vocational: staff.linkTovocational,
    educational: staff.linkToeducational,
    social: staff.linkTosocial,
    interpersonal: staff.linkTointerpersonal,
    selfcare: staff.linkToselfcare,
    medical: staff.linkTomedical,
    dental: staff.linkTodental,
    financial: staff.linkTofinancial,
    housing: staff.linkTohousing,
    transportation: staff.linkTotransportation,
    other: staff.linkToother,
  };

  const orderedRows = expectedRows.map((r) => ({
    label: r.label,
    status: pickText(statusByKey[r.key]) || "—",
    functioning:
      pickText(funcByKey[r.key], linkByKey[r.key], statusByKey[r.key]) ||
      "—",
  }));

  const plans = Array.from(
    { length: 14 },
    (_, i) => staff[`plan${i + 1}`]
  );

  const islicence = !!staff.islicence;

  const staffName = pickText(staff.staffName, "—");
  const supervisorName = pickText(staff.staffSignature, "—");
  const directorName = pickText(
    staff.directorSignature,
    form.treatmentdirectorURL,
    "—"
  );

  const staffRoleKey = staff.version || "";
  const supervisorRoleKey = staff.versionSuper || "";

  const clientSignedDate =
    form.patientSignatureDATE || staff.patientSignatureDATE;

  const staffSignatureMsg =
    pickText(form.message, staff.messageSupervisorSign) || "—";
  const supervisorSignatureMsg =
    pickText(form.messageSupervisorSign, form.messagetreatmentdirector) ||
    "—";
  const directorSignatureMsg =
    pickText(form.messagetreatmentdirector) || "—";

  return (
    <>
      {showSuccessAlert && (
        <>
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              zIndex: 1299,
            }}
          />
          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "300px",
              padding: "20px",
              backgroundColor: "#fff",
              boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
              borderRadius: "8px",
              textAlign: "center",
              zIndex: 1300,
            }}
          >
            <Typography variant="h6" style={{ marginBottom: "10px" }}>
              Form Saved
            </Typography>
            <Typography style={{ marginBottom: "20px" }}>
              Form saved successfully
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={handleClose}
            >
              OK
            </Button>
          </div>
        </>
      )}

      <Box sx={{ width: "100%" }}>
        <Box
          sx={{
            position: "sticky",
            top: "-16px",
            zIndex: (t) => t.zIndex.appBar,
            bgcolor: "#fff",
            p: { xs: 2, sm: 3, md: 4 },
            pb: 0,
            mb: 3,
          }}
        >
          {isMdDown ? (
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  fontSize: { xs: "0.7rem", sm: "0.8rem" },
                  mb: 1,
                }}
              >
                <strong>DOS:</strong> {fmt(dos)}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  fontSize: { xs: "0.9rem", sm: "1.1rem" },
                  mb: 1,
                  textAlign: "center",
                }}
              >
                {title}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ position: "relative" }}>
              <Typography
                variant="subtitle2"
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                }}
              >
                <strong>DOS:</strong> {fmt(dos)}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  position: "absolute",
                  top: 0,
                  left: "50%",
                  transform: "translateX(-50%)",
                  fontWeight: "bold",
                  whiteSpace: "nowrap",
                }}
              >
                {title}
              </Typography>
              <Box sx={{ height: 28 }} />
            </Box>
          )}
          <Divider />
        </Box>

        <Box sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          <Grid
            container
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Grid item xs={12} md={4}>
              <Typography
                variant="body2"
                sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
              >
                <strong>File #</strong> {fileVal}
              </Typography>
            </Grid>
            <Grid
              item
              xs={12}
              md={4}
              sx={{
                textAlign: { xs: "left", md: "center" },
                mt: { xs: 1, md: 0 },
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
              >
                <strong>Client Name:</strong> {nameVal}
              </Typography>
            </Grid>
            <Grid
              item
              xs={12}
              md={4}
              sx={{
                textAlign: { xs: "left", md: "right" },
                mt: { xs: 1, md: 0 },
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
              >
                <strong>DOB:</strong> {fmt(dobVal)}
              </Typography>
            </Grid>
          </Grid>
          <Divider sx={{ mb: 2 }} />
        </Box>

        <Box
          sx={{ mt: 1, px: { xs: 2, sm: 3, md: 4 }, mb: 1 }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                md: "repeat(3, minmax(0, 1fr))",
              },
              gap: 2,
            }}
          >
            <TextField
              label="Recipient Name"
              value={nameVal || "—"}
              size="small"
              fullWidth
              multiline
              minRows={1}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Client DOB"
              value={fmt(dobVal)}
              size="small"
              fullWidth
              multiline
              minRows={1}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Client ID#"
              value={clientID || "—"}
              size="small"
              fullWidth
              multiline
              minRows={1}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Time of Functional Assessment"
              value={timeFA || "—"}
              size="small"
              fullWidth
              multiline
              minRows={1}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Functional Assessment Date"
              value={fmt(faDate)}
              size="small"
              fullWidth
              multiline
              minRows={1}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Diagnostic Assessment Date"
              value={fmt(diagnosticDate)}
              size="small"
              fullWidth
              multiline
              minRows={1}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </Box>

        <Box
          sx={{ px: { xs: 2, sm: 3, md: 4 }, mt: 2, mb: 1 }}
        >
          <Typography
            variant="body2"
            sx={{
              lineHeight: 1.6,
              color: "text.secondary",
              textAlign: "justify",
              mb: 0.5,
              fontSize: { xs: "0.85rem", sm: "0.875rem" },
            }}
          >
            This Functional Assessment Tool is designed to identify areas of
            strength and need which can be addressed by Rehabilitation Services
            and/or Targeted Case Management Services and included in a treatment
            plan to remediate the symptoms of mental illness. The following are
            statements about some areas of the recipient&apos;s life.
          </Typography>
          <Typography
            variant="body2"
            sx={{
              lineHeight: 1.6,
              color: "text.secondary",
              textAlign: "justify",
              fontSize: { xs: "0.85rem", sm: "0.875rem" },
            }}
          >
            Please fill in the box next to the response that you think is the
            closest rating to how the recipient is currently functioning.
          </Typography>
        </Box>

        <Box sx={{ mt: 1, px: { xs: 2, sm: 3, md: 4 } }}>
          <TableContainer sx={{ width: "100%", border: "1px solid #ddd" }}>
            <Table
              size="small"
              sx={{ width: "100%", borderCollapse: "collapse" }}
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      border: "1px solid #ddd",
                      fontWeight: 700,
                      width: { md: "25%" },
                    }}
                  >
                    Field
                  </TableCell>
                  <TableCell
                    sx={{ border: "1px solid #ddd", fontWeight: 700 }}
                  >
                    Status
                  </TableCell>
                  <TableCell
                    sx={{ border: "1px solid #ddd", fontWeight: 700 }}
                  >
                    Functioning
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orderedRows.map((row, idx) => (
                  <TableRow key={idx}>
                    <TableCell
                      sx={{
                        border: "1px solid #ddd",
                        verticalAlign: "top",
                        fontWeight: 700,
                        whiteSpace: "pre-line",
                      }}
                    >
                      {row.label}
                    </TableCell>
                    <TableCell
                      sx={{
                        border: "1px solid #ddd",
                        verticalAlign: "top",
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {row.status || "—"}
                    </TableCell>
                    <TableCell
                      sx={{
                        border: "1px solid #ddd",
                        verticalAlign: "top",
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {row.functioning || "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Box
          sx={{ mt: 2, px: { xs: 2, sm: 3, md: 4 } }}
        >
          <Typography
            sx={{ m: 0, fontSize: { xs: "0.85rem", sm: "1rem" } }}
          >
            Check which of the areas are addressed in the Treatment Plan:
          </Typography>
          <Grid container spacing={1} sx={{ mt: 0.5 }}>
            {plans.map((checked, i) => (
              <Grid key={i} item xs="auto">
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Checkbox
                    checked={!!checked}
                    disabled
                    size="small"
                    sx={{ p: 0, mr: 1 }}
                  />
                  <Typography
                    sx={{
                      m: 0,
                      fontSize: { xs: "0.85rem", sm: "1rem" },
                    }}
                  >
                    {i + 1}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Box
          sx={{ mt: 2, px: { xs: 2, sm: 3, md: 4 } }}
        >
          <Typography
            sx={{ m: 0, fontSize: { xs: "0.85rem", sm: "1rem" } }}
          >
            Summarize the client's strengths, resources, and all areas of
            functional impairment:
          </Typography>
          <TextareaAutosize
            minRows={3}
            value={staff.functionalImpairment || ""}
            readOnly
            style={{
              width: "100%",
              borderRadius: "8px",
              border: "2px solid #000",
              padding: "10px",
              resize: "none",
              marginTop: "6px",
              fontSize: isSmDown ? "0.85rem" : "1rem",
            }}
          />
        </Box>

        <Box
          sx={{
            mt: 1,
            px: { xs: 2, sm: 3, md: 4 },
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            rowGap: 1,
          }}
        >
          <Checkbox
            checked={!!islicence}
            disabled
            size="small"
            sx={{ p: 0, mr: 1 }}
          />
          <Typography
            sx={{ m: 0, fontSize: { xs: "0.85rem", sm: "1rem" } }}
          >
            License holder did not contact the client's family and other natural
            supports
          </Typography>
        </Box>

        <Box
          sx={{ mt: 3, px: { xs: 2, sm: 3, md: 4 }, pb: 4 }}
        >
          <Grid
            container
            spacing={2}
            alignItems="center"
            sx={{ mb: 3 }}
          >
            <Grid item xs={12} sm={2}>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: "0.85rem", sm: "1rem" },
                }}
              >
                Client
              </Typography>
            </Grid>

            <Grid item xs={12} sm={10}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                {patientSigLink ? (
                  <>
                    <Box
                      sx={{
                        width: 260,
                        height: 90,
                        border: "2px solid #000",
                        borderRadius: 1.5,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#f5f5f5",
                        overflow: "hidden",
                      }}
                    >
                      <img
                        src={patientSigLink}
                        alt="Client Signature"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                        }}
                      />
                    </Box>
                    {clientSignedDate && (
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: { xs: "0.85rem", sm: "1rem" },
                        }}
                      >
                        Signed on{" "}
                        {moment(clientSignedDate).format(
                          "MM/DD/YYYY hh:mm A"
                        )}
                      </Typography>
                    )}
                    <Tooltip title="Edit Signature" arrow>
                      <Button
                        onClick={openClientModal}
                        variant="outlined"
                        size="small"
                        style={{ minWidth: "32px", padding: "4px" }}
                        aria-label="Edit Signature"
                      >
                        <EditIcon />
                      </Button>
                    </Tooltip>

                    <Tooltip title="Remove Signature" arrow>
                      <Button
                        onClick={handleDeleteClientSignature}
                        style={{ minWidth: "32px", padding: "4px" }}
                        aria-label="Remove Signature"
                      >
                        <RemoveCircleOutlineIcon style={{ color: "red" }} />
                      </Button>
                    </Tooltip>
                  </>
                ) : (
                  <Link component="button" onClick={openClientModal}>
                    Open signing window
                  </Link>
                )}
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ mb: 1.5 }}>
            <Grid
              container
              spacing={2}
              alignItems="center"
            >
              <Grid item xs={12} md={2.5}>
                <Typography
                  sx={{
                    fontWeight: "bold",
                    fontSize: { xs: "0.75rem", sm: "0.85rem" },
                  }}
                >
                  Staff Name:
                </Typography>
              </Grid>
              <Grid item xs={12} md={9.5}>
                <TextField
                  size="small"
                  value={staffName}
                  fullWidth
                  multiline
                  minRows={1}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
            </Grid>

            <Grid
              container
              spacing={1}
              sx={{
                mt: 0.5,
                pl: { xs: 0, md: `${LABEL_COL_WIDTH}px` },
              }}
            >
              {staffRoles.map((r) => (
                <Grid key={r.key} item xs="auto">
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Checkbox
                      checked={staffRoleKey === r.key || !!staff[r.key]}
                      disabled
                      size="small"
                      sx={{ p: 0, mr: 1 }}
                    />
                    <Typography
                      sx={{
                        m: 0,
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                      }}
                    >
                      {r.label}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>

          <Grid
            container
            spacing={2}
            alignItems="baseline"
            sx={{ mb: 3 }}
          >
            <Grid item xs={12} md={3}>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: "0.85rem", sm: "1rem" },
                }}
              >
                Staff Signature (required):
              </Typography>
            </Grid>
            <Grid item xs={12} md={9}>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: "0.85rem", sm: "1rem" },
                  wordBreak: "break-word",
                }}
              >
                {staffSignatureMsg}
              </Typography>
            </Grid>
          </Grid>

          <Box sx={{ mb: 1.5 }}>
            <Grid
              container
              spacing={2}
              alignItems="center"
            >
              <Grid item xs={12} md={2.5}>
                <Typography
                  sx={{
                    fontWeight: "bold",
                    fontSize: { xs: "0.75rem", sm: "0.85rem" },
                  }}
                >
                  Clinical Supervisor:
                </Typography>
              </Grid>
              <Grid item xs={12} md={9.5}>
                <TextField
                  size="small"
                  value={supervisorName}
                  fullWidth
                  multiline
                  minRows={1}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
            </Grid>

            <Grid
              container
              spacing={1}
              sx={{
                mt: 0.5,
                pl: { xs: 0, md: `${LABEL_COL_WIDTH}px` },
              }}
            >
              {supervisorRoles.map((r) => (
                <Grid key={r.key} item xs="auto">
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Checkbox
                      checked={supervisorRoleKey === r.key || !!staff[r.key]}
                      disabled
                      size="small"
                      sx={{ p: 0, mr: 1 }}
                    />
                    <Typography
                      sx={{
                        m: 0,
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                      }}
                    >
                      {r.label}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>

          <Grid
            container
            spacing={2}
            alignItems="baseline"
            sx={{ mb: 3 }}
          >
            <Grid item xs={12} md={5}>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: "0.85rem", sm: "1rem" },
                }}
              >
                Clinical Supervisor/MH Professional Signature:
              </Typography>
            </Grid>
            <Grid item xs={12} md={7}>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: "0.85rem", sm: "1rem" },
                  wordBreak: "break-word",
                }}
              >
                {supervisorSignatureMsg}
              </Typography>
            </Grid>
          </Grid>

          <Grid
            container
            spacing={2}
            alignItems="center"
            sx={{ mb: 1.5 }}
          >
            <Grid item xs={12} md={4}>
              <Typography
                sx={{
                  fontWeight: "bold",
                  fontSize: { xs: "0.75rem", sm: "0.85rem" },
                }}
              >
                ARMHS Treatment Director Name:
              </Typography>
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                size="small"
                value={directorName}
                fullWidth
                multiline
                minRows={1}
                InputProps={{ readOnly: true }}
              />
            </Grid>
          </Grid>

          <Grid
            container
            spacing={2}
            alignItems="baseline"
            sx={{ mb: 3 }}
          >
            <Grid item xs={12} md={4}>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: "0.85rem", sm: "1rem" },
                }}
              >
                ARMHS Treatment Director Signature:
              </Typography>
            </Grid>
            <Grid item xs={12} md={8}>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: "0.85rem", sm: "1rem" },
                  wordBreak: "break-word",
                }}
              >
                {directorSignatureMsg}
              </Typography>
            </Grid>
          </Grid>

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveForm}
            >
              Save Form
            </Button>
          </Box>
        </Box>
      </Box>

      <Dialog
        open={openClientSignatureModal}
        onClose={closeClientModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Signature</DialogTitle>
        <DialogContent>
          <Box sx={{ border: "1px solid #000", mt: 1 }}>
            <SignatureField
              ref={sigCanvasRef}
              
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <Button
            variant="contained"
            onClick={handleSaveClientSignature}
            sx={{ minWidth: 120, mr: 2 }}
          >
            Save
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (sigCanvasRef.current) {
                sigCanvasRef.current.clear();
              }
            }}
            sx={{ minWidth: 120 }}
          >
            Clear
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ARMHSFunctionalAssessmentPreview;